import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faBars, faComment, faHouse } from "@fortawesome/free-solid-svg-icons";
import { faTelegramPlane, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { MobileSheet } from "../../ui/components/MobileSheet";
import { Button } from "../../ui/components/Button";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { signOut } from "../../store/slices/authSlice";
import { toast } from "react-toastify";
import styled from "styled-components";
import { useSpecialButtons } from "../../ui/specialButtons/SpecialButtonsContext";
import { haptic } from "../../utils/haptics";
import { IosHapticOverlay } from "../../ui/components/IosHapticOverlay";
import { apiClient } from "../../api/client";
import { url } from "../../api";
import { AUTH_URL } from "../../config/env";
import { FOOTER_LINKS, NavLinkItem, isNavGroup, isNavItemActive, navEntriesFor } from "./navItems";
import { StoreCount } from "../../features/marketplace";
import { useStoreOpen } from "../../features/storeSwitch/storeOpen";

// openChatBubble deprecated — replaced by agent widget event
const openAgentChat = () => {
  requestAnimationFrame(() => {
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("n450s:open-agent-chat"));
    }, 50);
  });
};

// hook to detect mobile viewport
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 700);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

// hook to check legacy layout preference
const useLegacyLayout = () => {
  const [useLegacy, setUseLegacy] = useState(() => {
    const stored = localStorage.getItem("useLegacyLayout");
    return stored === "true";
  });

  return useLegacy;
};

const MobileNavBar: React.FC = () => {
  const location = useLocation();
  // every page is one tap away: direct pages share a tile grid up top, and each
  // group gets its own labelled grid instead of an accordion
  const navEntries = navEntriesFor(useStoreOpen());
  const directLinks = navEntries.filter((entry): entry is NavLinkItem => !isNavGroup(entry));
  const navGroups = navEntries.filter(isNavGroup);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth);
  const isAuthReady = user.bootstrapStatus === "ready";
  const isMobile = useIsMobile();
  const useLegacy = useLegacyLayout();
  const [isOpen, setIsOpen] = useState(false);
  const [chatSheetOpen, setChatSheetOpen] = useState(false);
  // on iOS a pan that starts on an IosHapticOverlay never scrolls, so the
  // tiles only carry one while the sheet fits without scrolling. Starts true
  // so the first open never blocks a scroll before the sheet has measured.
  const [sheetScrolls, setSheetScrolls] = useState(true);
  const specialButtonsContext = useSpecialButtons();
  const special1 = specialButtonsContext ? specialButtonsContext.special1 : null;
  const special2 = specialButtonsContext ? specialButtonsContext.special2 : null;
  const special3 = specialButtonsContext ? specialButtonsContext.special3 : null;

  // hide bottom bar when the chat sheet is open so it doesn't
  // jump up with the virtual keyboard on mobile
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setChatSheetOpen(!!detail?.open);
    };
    window.addEventListener("n450s:chat-sheet-change", handler);
    return () => window.removeEventListener("n450s:chat-sheet-change", handler);
  }, []);

  // any navigation closes the menu, including back/forward and links outside it
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isMobile) setIsOpen(false);
  }, [isMobile]);

  // the sitebot chip floats above the sheet; it steps aside while the menu is open
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("n450s:nav-sheet-change", { detail: { open: isOpen } }));
    return () => {
      window.dispatchEvent(new CustomEvent("n450s:nav-sheet-change", { detail: { open: false } }));
    };
  }, [isOpen]);

  // body scroll lock is handled by MobileSheet

  // if not mobile or legacy layout is enabled, don't render
  if (!isMobile || useLegacy) {
    return null;
  }

  const handleSignOut = async () => {
    try {
      await apiClient.post(`${url}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.warn("Failed to revoke session on server:", error);
    }
    dispatch(signOut());
    toast("Hope to see you again soon!");
    setIsOpen(false);

    // redirect to Auth's RP-initiated logout to destroy the SSO session,
    // otherwise the next login auto-authenticates without showing a login form
    const postLogoutUri = encodeURIComponent(window.location.origin);
    window.location.href = `${AUTH_URL}/oauth/logout?client_id=n450s_web&post_logout_redirect_uri=${postLogoutUri}`;
  };

  // the route effect closes the sheet too, but tapping the page you are
  // already on doesn't change the route
  const handleNavClick = () => {
    haptic("selection");
    setIsOpen(false);
  };

  const handleChatClick = () => {
    haptic("light");
    setIsOpen(false);
    setTimeout(() => {
      openAgentChat();
    }, 300);
  };

  // wrapper to close sheet when bottom bar actions are triggered
  const handleSpecial1Click = () => {
    haptic("light");
    if (special1?.onClick) {
      special1.onClick();
    }
    setIsOpen(false);
  };

  const handleSpecial2Click = () => {
    haptic("light");
    if (special2?.onClick) {
      special2.onClick();
    } else {
      navigate('/');
    }
    setIsOpen(false);
  };

  const handleSpecial3Click = () => {
    haptic("light");
    if (special3?.onClick) {
      special3.onClick();
    }
    setIsOpen(false);
  };

  const renderTiles = (items: NavLinkItem[]) =>
    items.map((item, index) => {
      const active = isNavItemActive(location.pathname, item);
      // an odd one out takes the whole row instead of leaving a hole
      const fullRow = items.length % 2 === 1 && index === items.length - 1;
      return (
        <NavTile
          key={item.to}
          to={item.to}
          onClick={handleNavClick}
          aria-current={active ? "page" : undefined}
          $active={active}
          $fullRow={fullRow}
        >
          <IosHapticOverlay disabled={sheetScrolls} />
          {item.name}
          {item.to === "/marketplace" && <StoreCount />}
        </NavTile>
      );
    });

  return (
    <>
      {/* bottom app bar — hidden when chat sheet is open to avoid keyboard push */}
      {!chatSheetOpen && <BottomAppBar>
        <MenuButton
          onClick={() => {
            haptic("light");
            setIsOpen(!isOpen);
          }}
          aria-label={isOpen ? "Close menu" : "Open menu"}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          <IosHapticOverlay />
          {isOpen ? (
            <>
              <FontAwesomeIcon icon={faAngleDown} size="lg" />
              <span>close</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faBars} size="lg" />
              <span>menu</span>
            </>
          )}
        </MenuButton>
        <RightActions>
          {/* special3 - icon button, can show text if showText is true */}
          {special3 && (
            special3.showText && special3.label ? (
              <Special1Button
                onClick={handleSpecial3Click}
                disabled={special3.disabled}
                aria-label={special3.ariaLabel || special3.label || "Special action"}
              >
                <IosHapticOverlay disabled={special3.disabled} />
                {special3.icon && (
                  <FontAwesomeIcon icon={special3.icon} style={{ marginRight: "8px" }} />
                )}
                <span>{special3.label}</span>
              </Special1Button>
            ) : (
              <Special2Button
                onClick={handleSpecial3Click}
                disabled={special3.disabled}
                aria-label={special3.ariaLabel || special3.label || "Special action"}
              >
                <IosHapticOverlay disabled={special3.disabled} />
                {special3.icon && <FontAwesomeIcon icon={special3.icon} />}
              </Special2Button>
            )
          )}
          {/* special2 - can show text if showText is true. The home (house)
              fallback is suppressed while special3 (the AOTD refresh) is active,
              so the refresh icon takes the home icon's place. */}
          {special2 ? (
            special2.showText && special2.label ? (
              <Special1Button
                onClick={handleSpecial2Click}
                disabled={special2.disabled}
                aria-label={special2.ariaLabel || special2.label || "Special action"}
              >
                <IosHapticOverlay disabled={special2.disabled} />
                {special2.icon && (
                  <FontAwesomeIcon icon={special2.icon} style={{ marginRight: "8px" }} />
                )}
                <span>{special2.label}</span>
              </Special1Button>
            ) : (
              <Special2Button
                onClick={handleSpecial2Click}
                disabled={special2.disabled}
                aria-label={special2.ariaLabel || special2.label || "Special action"}
              >
                <IosHapticOverlay disabled={special2.disabled} />
                {special2.icon ? (
                  <FontAwesomeIcon icon={special2.icon} />
                ) : (
                  <FontAwesomeIcon icon={faHouse} />
                )}
              </Special2Button>
            )
          ) : special3 ? null : (
            <Special2Button onClick={handleSpecial2Click} aria-label="Home">
              <IosHapticOverlay />
              <FontAwesomeIcon icon={faHouse} />
            </Special2Button>
          )}
          {/* special1 - can have text based on showText prop */}
          {special1 && (
            <Special1Button
              onClick={handleSpecial1Click}
              disabled={special1.disabled}
              aria-label={special1.ariaLabel || special1.label || "Special action"}
            >
              <IosHapticOverlay disabled={special1.disabled} />
              {special1.icon && (
                <FontAwesomeIcon icon={special1.icon} style={{ marginRight: (special1.showText !== false && special1.label) ? "8px" : "0" }} />
              )}
              {special1.showText !== false && special1.label && <span>{special1.label}</span>}
            </Special1Button>
          )}
        </RightActions>
      </BottomAppBar>}

      {/* mobile sheet: as tall as its content, scrolls only past the cap */}
      <MobileSheet
        open={isOpen}
        onOpenChange={setIsOpen}
        fitContent
        maxHeight="78vh"
        showGrabber
        contentClassName="px-0 pb-0"
        onScrollableChange={setSheetScrolls}
      >
        <SheetContent>
          {/* primary navigation */}
          <NavTiles aria-label="site">
            <TileGrid>{renderTiles(directLinks)}</TileGrid>
            {navGroups.map((group) => (
              <div key={group.name}>
                <GroupLabel>{group.name}</GroupLabel>
                <TileGrid>{renderTiles(group.items)}</TileGrid>
              </div>
            ))}
          </NavTiles>

          {/* auth section */}
          <Divider />
          <AuthRow>
            <ChatIconButton onClick={handleChatClick} aria-label="chat">
              <IosHapticOverlay disabled={sheetScrolls} />
              <FontAwesomeIcon icon={faComment} />
            </ChatIconButton>
            <AuthButtonsRight>
              {!isAuthReady ? null : user._id ? (
                <>
                  <AuthProfileLink to="/profile" onClick={handleNavClick}>
                    <AuthProfileButton $active={location.pathname === "/profile"}>
                      profile
                    </AuthProfileButton>
                  </AuthProfileLink>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    iosTapOverlay={!sheetScrolls}
                    onClick={handleSignOut}
                  >
                    logout
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="default" asChild>
                    <Link to="/register" onClick={handleNavClick}>
                      signup
                    </Link>
                  </Button>
                  <Button size="sm" variant="default" asChild>
                    <Link to="/login" onClick={handleNavClick}>
                      login
                    </Link>
                  </Button>
                </>
              )}
            </AuthButtonsRight>
          </AuthRow>

          {/* social + small print share one row */}
          <Divider />
          <FooterRow>
            <SocialLinks>
              <SocialLink
                href="https://x.com/n450s"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="n450s on X"
              >
                <FontAwesomeIcon icon={faXTwitter} />
              </SocialLink>
              <SocialLink
                href="https://t.me/+CxzdB8kP_3RlZGQ0"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="n450s on Telegram"
              >
                <FontAwesomeIcon icon={faTelegramPlane} />
              </SocialLink>
            </SocialLinks>
            <FooterLinks>
              {FOOTER_LINKS.map((link) => (
                <FooterLink key={link.to} to={link.to} onClick={handleNavClick}>
                  {link.name}
                </FooterLink>
              ))}
            </FooterLinks>
          </FooterRow>

          {/* rights text */}
          <RightsText>
            site owner/creator: owner of n450s.com domain. username: n450s. all rights.
          </RightsText>
        </SheetContent>
      </MobileSheet>
    </>
  );
};

// styled components
const BottomAppBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  background-color: white;
  border-top: 10px solid rgba(0, 0, 0, 0.7);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);

  @media (min-width: 701px) {
    display: none;
  }
`;

const MenuButton = styled.button`
  /* hosts the iOS haptic overlay (see IosHapticOverlay) */
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: #9beece;
  border: var(--hm-border);
  box-shadow: var(--hm-shadow-sm);
  font-size: 1rem;
  font-weight: 600;
  color: rgb(35, 35, 35);
  cursor: pointer;
  transition: all 0.2s;
  min-height: 48px;

  &:active {
    transform: var(--hm-press-translate);
    box-shadow: var(--hm-shadow-sm-pressed);
  }
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChatIconButton = styled.button`
  /* hosts the iOS haptic overlay (see IosHapticOverlay) */
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  background-color: white;
  border: var(--hm-border);
  box-shadow: var(--hm-shadow-sm);
  color: rgb(35, 35, 35);
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;

  &:active {
    transform: var(--hm-press-translate);
    box-shadow: var(--hm-shadow-sm-pressed);
  }

  svg {
    font-size: 1.2rem;
  }
`;

const SheetContent = styled.div`
  padding: 4px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NavTiles = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const GroupLabel = styled.div`
  margin: 0 0 6px 2px;
  font-size: 0.75rem;
  color: #666;
`;

const NavTile = styled(Link)<{ $active: boolean; $fullRow: boolean }>`
  /* hosts the iOS haptic overlay (see IosHapticOverlay) */
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  grid-column: ${(props) => (props.$fullRow ? "1 / -1" : "auto")};
  min-height: 44px;
  padding: 10px 12px;
  background-color: ${(props) => (props.$active ? "#9beece" : "white")};
  border: var(--hm-border-width) solid ${(props) => (props.$active ? "#466a5d" : "#ccc")};
  box-shadow: ${(props) => (props.$active ? "var(--hm-shadow-sm)" : "var(--hm-shadow-sm-pressed)")};
  font-size: 0.95rem;
  line-height: 1.2;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.15s, box-shadow 0.1s, transform 0.1s;

  &,
  &:hover,
  &:focus,
  &:visited,
  &:active {
    color: ${(props) => (props.$active ? "black" : "#3f3f3f")};
    text-decoration: none;
  }

  &:active {
    background-color: ${(props) => (props.$active ? "#9beece" : "#d4f9eb")};
    transform: var(--hm-press-translate);
    box-shadow: var(--hm-shadow-sm-pressed);
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: rgba(0, 0, 0, 0.2);
`;

const FooterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 16px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 4px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  font-size: 1.1rem;
  color: #3f3f3fff;
  transition: color 0.2s;

  &:active {
    color: #ff4be1;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0 12px;
`;

const Special2Button = styled.button`
  /* hosts the iOS haptic overlay (see IosHapticOverlay) */
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  background-color: white;
  border: var(--hm-border);
  box-shadow: var(--hm-shadow-sm);
  color: rgb(35, 35, 35);
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;

  &:active:not(:disabled) {
    transform: var(--hm-press-translate);
    box-shadow: var(--hm-shadow-sm-pressed);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 1.2rem;
  }
`;

const Special1Button = styled.button`
  /* hosts the iOS haptic overlay (see IosHapticOverlay) */
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
  background-color: white;
  border: var(--hm-border);
  box-shadow: var(--hm-shadow-sm);
  font-size: 1rem;
  color: rgb(35, 35, 35);
  cursor: pointer;
  transition: all 0.2s;
  min-height: 48px;
  border-radius: 4px;

  &:active:not(:disabled) {
    transform: var(--hm-press-translate);
    box-shadow: var(--hm-shadow-sm-pressed);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FooterLink = styled(Link)`
  padding: 8px 0;
  font-size: 0.75rem;
  color: #666;
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: #3f3f3fff;
    text-decoration: underline;
  }

  &:active {
    color: #ff4be1;
  }
`;

const RightsText = styled.p`
  margin: 0;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 9px;
  color: gray;
  text-align: center;
`;

// auth row container for side-by-side buttons
const AuthRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

// wrapper for right-side auth buttons
const AuthButtonsRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  margin-left: auto;
`;

const AuthProfileLink = styled(Link)`
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }

  &:active {
    text-decoration: none;
  }

  &:focus {
    text-decoration: none;
  }
`;

const AuthProfileButton = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  box-shadow: none;
  padding: 0;
  margin: 0;
  color: ${(props) => (props.$active ? "black" : "#3f3f3fff")};
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: flex;
  align-items: center;
  height: 48px;

  &:hover {
    font-weight: 900;
    filter: drop-shadow(0px 0px 1px #000000);
    text-decoration: none;
  }

  &:active {
    color: #ff4be1;
    text-decoration: none;
  }
`;

export default MobileNavBar;
